import {
  Avatar,
  Button,
  Card,
  Col,
  Divider,
  EditButton,
  Icons,
  ListButton,
  notification,
  Popconfirm,
  Row,
  Show,
  Space,
  Table,
  Tag,
  Typography,
} from '@pankod/refine-antd';
import { IResourceComponentsProps, useCan, useNavigation } from '@pankod/refine-core';
import { useParams } from '@pankod/refine-react-router-v6';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { ProductVariantShowDrawer } from 'components/drawer';
import { useEffect, useState } from 'react';
import { getProductNew, getVariantsOfProduct, IProductNew } from 'services/product';
import { IVariant, deleteVariant } from 'services/variant';

export const ProductShow: React.FC<IResourceComponentsProps> = () => {
  const [product, setProduct] = useState<IProductNew>();
  const [variants, setVariants] = useState<IVariant[]>([]);
  const [isProductLoading, setIsProductLoading] = useState<boolean>(false);
  const [isShowDrawerVisible, setIsShowDrawerVisible] = useState<boolean>(false);
  const [variantToShow, setVariantToShow] = useState<IVariant | null>(null);
  const { id: productId } = useParams();
  const { push } = useNavigation();
  const { data: readPurchasePrice } = useCan({
    resource: 'purchase_price',
    action: 'read',
  });

  const getProductInfo = async (productId: string) => {
    const data = await getProductNew(productId);
    setProduct(data);
  };

  const getVariants = async (productId: string) => {
    const response = await getVariantsOfProduct(productId);

    const productVariants = response === null ? [] : response.productVariants;
    const modifiedVariants = productVariants.map((item) => {
      const { photo, ...rest } = item;
      return {
        ...rest,
        productVariantPhoto: photo,
      };
    });
    setVariants(modifiedVariants);
  };

  useEffect(() => {
    if (!productId) return;

    (async () => {
      setIsProductLoading(true);
      try {
        await getProductInfo(productId);
        await getVariants(productId);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsProductLoading(false);
    })();
  }, [productId]);

  const productTags =
    product?.productTags.map((item) => ({ id: item.tag.id, name: item.tag.name })) || [];
  const productTemplates =
    product?.productType.map((item) => ({
      id: item.productTemplate.id,
      name: item.productTemplate.name,
    })) || [];
  const productCategory = product?.productCategory;
  const customFields = (product?.customFields ?? []).map((customField) => (
    <Tag key={customField.name} color="default">
      {customField.name.toUpperCase()}
    </Tag>
  ));

  const tags = productTags.map((tag) => (
    <Tag key={tag.id} color="default">
      {tag.name.toUpperCase()}
    </Tag>
  ));

  const templateTags = productTemplates.map((template) => (
    <Tag key={template.id} color="default">
      {template.name}
    </Tag>
  ));

  const category = productCategory ? (
    <Tag key={productCategory.id} color="blue">
      {productCategory.name}
    </Tag>
  ) : null;

  const headerButtons = (
    <Space>
      <ListButton resourceNameOrRouteName="products" />
      <Button
        onClick={() => push(`/products/${productId}/variants/create`)}
        icon={<Icons.PlusSquareOutlined />}
      >
        Create variant
      </Button>
    </Space>
  );

  const onDelete = async (id: string) => {
    if (!productId) return;

    try {
      await deleteVariant(id);
      await getVariants(productId);
      notification.success({
        message: 'Success',
        description: 'Successfully deleted a variant',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: err instanceof Error ? err.message : null,
      });
    }
  };

  return (
    <>
      <Show title="Show Product" headerProps={{ extra: headerButtons }}>
        <Row gutter={[48, 32]} wrap>
          <Col xs={{ span: 24, order: 2 }} lg={{ span: 17, order: 1 }}>
            <Card title="Information" loading={isProductLoading}>
              <Typography.Title level={5}>Product Name</Typography.Title>
              <Typography.Text>{product?.name}</Typography.Text>
              <Typography.Title level={5}>SKU</Typography.Title>
              <Typography.Text>{product?.sku}</Typography.Text>
              {readPurchasePrice?.can && (
                <>
                  <Typography.Title level={5}>Suggested Purchase Price</Typography.Title>
                  <Typography.Text>{product?.purchasePrice}</Typography.Text>
                </>
              )}
              <Typography.Title level={5}>Suggested Selling Price</Typography.Title>
              <Typography.Text>{product?.sellingPrice}</Typography.Text>
              <Typography.Title level={5}>Product Details</Typography.Title>
              <Typography.Text>{product?.description}</Typography.Text>
              <Typography.Title level={5}>Templates</Typography.Title>
              <div style={{ marginBottom: '1.3rem' }}>{templateTags}</div>
              <Typography.Title level={5}>Custom Fields</Typography.Title>
              <div style={{ marginBottom: '1.3rem' }}>{customFields}</div>
              <Typography.Title level={5}>Category</Typography.Title>
              <div style={{ marginBottom: '1.3rem' }}>{category}</div>
              <Typography.Title level={5}>Tags</Typography.Title>
              <div style={{ marginBottom: '1.3rem' }}>{tags}</div>
            </Card>
          </Col>

          <Col xs={{ span: 24, order: 1 }} lg={{ span: 7, order: 2 }}>
            <Card
              style={{
                backgroundColor: '#FAFAFA',
                opacity: 1,
              }}
              bodyStyle={{ height: '300px' }}
            >
              <Space
                direction="vertical"
                align="center"
                style={{
                  width: '100%',
                  textAlign: 'center',
                  margin: '26px 0 30px 0',
                }}
              >
                <Avatar
                  src={product?.photo?.url || defaultAvatar}
                  size={200}
                  style={{ objectFit: 'cover' }}
                  alt={`Avatar`}
                />
              </Space>
            </Card>
          </Col>
        </Row>
      </Show>

      <Divider />

      <Row wrap={false}>
        <Col flex="auto">
          <ProductVariantShowDrawer
            variant={variantToShow}
            visible={isShowDrawerVisible}
            onClose={() => {
              setIsShowDrawerVisible(false);
              setVariantToShow(null);
            }}
          />

          <Table
            dataSource={variants}
            loading={isProductLoading}
            rowKey="productVariantId"
            scroll={{ x: true }}
          >
            <Table.Column dataIndex="sku" title="SKU" />
            <Table.Column<IVariant>
              dataIndex="avatar"
              title="Image"
              width="6%"
              render={(_text, record): React.ReactNode => {
                return (
                  <Avatar
                    size={36}
                    style={{ objectFit: 'cover' }}
                    src={record.productVariantPhoto?.thumbUrl || defaultAvatar}
                    alt={`Avatar`}
                  />
                );
              }}
            />
            <Table.Column dataIndex="productVariantName" title="Name" />
            {readPurchasePrice?.can && (
              <Table.Column dataIndex="purchasePrice" title="Suggested Purchase Price" />
            )}
            <Table.Column dataIndex="sellingPrice" title="Suggested Selling Price" />
            <Table.Column<IVariant>
              dataIndex="productTemplates"
              title="Templates"
              render={(value) => {
                return value.map((item: any) => (
                  <Tag key={item.templateId}>{item.templateName}</Tag>
                ));
              }}
            />
            <Table.Column<IVariant>
              title="Actions"
              dataIndex="actions"
              render={(_text, record) => {
                return (
                  <Space>
                    <Button
                      size="small"
                      icon={<Icons.ArrowRightOutlined />}
                      onClick={() => {
                        setIsShowDrawerVisible(true);
                        setVariantToShow(record);
                      }}
                    />
                    <EditButton
                      resourceNameOrRouteName="variants"
                      recordItemId={record.productVariantId}
                      size="small"
                      hideText
                    />
                    <Popconfirm
                      title="Are you sure?"
                      okText="Delete"
                      onConfirm={() => onDelete(record.productVariantId)}
                    >
                      <Button size="small" danger icon={<Icons.DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                );
              }}
            />
          </Table>
        </Col>
      </Row>
    </>
  );
};
